import { inject, Injectable, Signal, signal } from '@angular/core';
import { CatalogsType } from '@models/catalogs.model';
import { take } from 'rxjs';
import { CatalogsService } from './catalogs.service';
import { UsersService } from './users.service';
import { UserProfile } from '@models/userDTO.model';
import { GetUserPermissionsResponse } from '@models/permissions.model';
import { ModulesService } from './modules.service';

type CatalogExtractors = {
  [K in CatalogsType]: {
    resultKey?: string;
  };
};

const catalogConfig: CatalogExtractors = {
  'genders/filters': { resultKey: 'data.genders' },
  'jobs/filters': { resultKey: 'data.jobs' },
  'last-grades/filters': { resultKey: 'data.last_grades' },
  'sepomex/states': { resultKey: 'data.states' },
  'cat-employee-corporations/get-all': { resultKey: 'cat_employee_corporations' },
  'courses/filters': { resultKey: undefined },
  'status-employees/filters': { resultKey: 'data.status_employee' },
  'type-staff/filters': { resultKey: 'data.type_staff' },
  'cat-patrol-types/get-all': { resultKey: 'cat_patrol_types' },
  'cat-patrol-marks/get-all': { resultKey: 'cat_patrol_marks' },
  'cat-patrol-submarks/get-all': { resultKey: 'cat_patrol_submarks' },
  'cat-patrol-transmissions/get-all': { resultKey: 'cat_patrol_transmissions' },
  'cat-patrol-states/get-all': { resultKey: 'cat_patrol_states' },
  'cat-weapon-types/get-all': { resultKey: 'cat_weapon_types' },
  'cat-weapon-status/get-all': { resultKey: 'cat_weapon_status' },
  'cat-weapon-models/get-all': { resultKey: 'cat_weapon_models' },
  'cat-weapon-marks/get-all': { resultKey: 'cat_weapon_marks' },
  'cat-weapon-operations/get-all': { resultKey: 'cat_weapon_operations' },
  'cat-weapon-calibers/get-all': { resultKey: 'cat_weapon_calibers' },
  'cat-weapon-classes/get-all': { resultKey: 'cat_weapon_classes' },
  'sepomex/municipalities': { resultKey: undefined },
  'sepomex/colonies': { resultKey: undefined },
  'cat-police-agencies/get-all': { resultKey: 'cat_police_agencies' },
  'cat-report-sources/get-all': { resultKey: 'cat_report_sources' },
  'cat-subincidences/get-motives-iph': { resultKey: 'cat_subincidences' },
  'cat-event-modalities/get-all': { resultKey: 'cat_event_modalities' },
  'cat-incidences/get-all': { resultKey: 'cat_incidences' },
  'cat-incidence-employee/filters': { resultKey: 'data.cat_incidence_employee' },
  'cat-subincidences/get-categories-iph': { resultKey: undefined }
};

@Injectable({
  providedIn: 'root'
})
export class StateService {

  /* SERVICES */
  #catalogsService = inject(CatalogsService);
  #usersService = inject(UsersService);
  #modulesService = inject(ModulesService);
  /* SERVICES */

  //* Mapa con signal para cachear resultados
  #catalogSignals = new Map<CatalogsType, Signal<unknown[]>>();
  #userProfileSignal = signal<UserProfile | null>(null);
  #userPermissionsSignal = signal<GetUserPermissionsResponse[] | null>(null);

  /**
   * @name getSignalCatalog
   * @description Obtiene un catálogo específico como una señal de Angular, utilizando caché para evitar solicitudes repetidas.
   * @template T Tipo de los elementos en el catálogo.
   * @param {CatalogsType} endpoint El endpoint del catálogo a obtener.
   * @returns {Signal<T[]>} Una señal que emitirá los datos del catálogo.
   */
  public getSignalCatalog<T = any>(endpoint: CatalogsType): Signal<T[]> {
    if (this.#catalogSignals.has(endpoint)) {
      return this.#catalogSignals.get(endpoint)! as Signal<T[]>;
    }

    const dataSignal = signal<T[]>([]);
    this.#catalogSignals.set(endpoint, dataSignal);

    this.#catalogsService.getCatalog(endpoint)
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const config = catalogConfig[endpoint];
          let result = res;
          if (config.resultKey) {
            for (const key of config.resultKey.split('.')) {
              result = result[key];
            }
          }

          dataSignal.set(Array.isArray(result) ? result as T[] : []);
        },
        error: () => dataSignal.set([])
      });

    return dataSignal;
  }

  /**
   * @name getUserProfileSignal
   * @description Devuelve la señal del usuario actual. Si aún no se ha cargado, la obtiene del servicio.
   * @returns {Promise<Signal<UserProfile | null>>}
   */
  public async getUserProfileSignal(): Promise<Signal<UserProfile | null>> {
    if (this.#userProfileSignal() !== null) {
      return this.#userProfileSignal;
    }

    try {
      const { data: [policeData] } = await this.#usersService.getUserProfile();
      this.#userProfileSignal.set(policeData);
    } catch (error) {
      this.#userProfileSignal.set(null);
    }

    return this.#userProfileSignal;
  }

  /**
   * @name getUserPermissionsByUUIDSignal
   * @description Devuelve la señal del usuario actual. Si aún no se ha cargado, la obtiene del servicio.
   * @returns {Promise<Signal<UserPermissionsByUUID | null>>}
   */
  public async getUserPermissionsByUUIDSignal(uuid: string): Promise<Signal<GetUserPermissionsResponse[] | null>> {
    if (this.#userPermissionsSignal() !== null) {
      return this.#userPermissionsSignal;
    }

    try {
      const data = await this.#modulesService.getUserPermissionsByUUID(uuid);
      this.#userPermissionsSignal.set(data);
    } catch (error) {
      this.#userPermissionsSignal.set(null);
    }

    return this.#userPermissionsSignal;
  }

  /**
   * @name getUserPermissionsSignal
   * @description User Permissions GETTER
   * @returns {Signal<GetUserPermissionsResponse[] | null>}
   */
  public getUserPermissionsSignal(): Signal<GetUserPermissionsResponse[] | null> {
    return this.#userPermissionsSignal;
  }

  /**
   * @name setUserPermissionsSignal
   * @description User Permissions SETTER
   * @params { permissions: GetUserPermissionsResponse[] }
   * @returns {void}
   */
  public setUserPermissionsSignal(permissions: GetUserPermissionsResponse[]): void {
    this.#userPermissionsSignal.set(permissions);
  }

  /**
   * @name clearCache
   * @description Limpia el caché de catálogos
   */
  public clearCache(): void {
    this.#catalogSignals.clear();
    this.#userProfileSignal.set(null);
    this.#userPermissionsSignal.set(null);
  }
}
